import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiCamera, FiFileText, FiDownload, FiExternalLink, FiHash, FiMapPin, FiThermometer, FiShield, FiClock, FiTruck, FiBarChart } from 'react-icons/fi';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, #1a1a3e 0%, #2d1b69 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const LotTitle = styled.div`
  h2 {
    color: #4facfe;
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }
  
  .lot-subtitle {
    color: #a0a0a0;
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalContent = styled.div`
  padding: 2rem;
`;

const TabNavigation = styled.div`
  display: flex;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: ${props => props.active ? '#4facfe' : '#a0a0a0'};
  border-bottom: 2px solid ${props => props.active ? '#4facfe' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: #4facfe;
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const DetailSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  
  h3 {
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  .label {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
  
  .value {
    color: #fff;
    font-weight: 600;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ImageCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
    border-color: #4facfe;
  }
  
  .image-placeholder {
    height: 150px;
    background: linear-gradient(45deg, #4facfe, #00f2fe);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 2rem;
  }
  
  .image-info {
    padding: 1rem;
    
    .image-title {
      color: #fff;
      font-weight: 600;
      margin-bottom: 0.3rem;
    }
    
    .image-date {
      color: #a0a0a0;
      font-size: 0.8rem;
    }
  }
`;

const DocumentList = styled.div`
  .document-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    margin-bottom: 1rem;
    
    .doc-info {
      display: flex;
      align-items: center;
      gap: 1rem;
      
      .doc-icon {
        font-size: 1.5rem;
        color: #4facfe;
      }
      
      .doc-details {
        .doc-name {
          color: #fff;
          font-weight: 600;
          margin-bottom: 0.3rem;
        }
        
        .doc-meta {
          color: #a0a0a0;
          font-size: 0.8rem;
        }
      }
    }
    
    .doc-actions {
      display: flex;
      gap: 0.5rem;
    }
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.primary ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(79, 172, 254, 0.3);
  }
`;

const BlockchainSection = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  .blockchain-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #00ff88;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  .hash-container {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    
    .hash-label {
      color: #00ff88;
      font-size: 0.8rem;
      margin-bottom: 0.5rem;
    }
    
    .hash-value {
      font-family: monospace;
      color: #fff;
      word-break: break-all;
      font-size: 0.9rem;
    }
  }
`;

function LotDetailModal({ lot, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!lot) return null;

  const mockImages = [
    { id: 1, title: 'Warehouse Storage View', date: '2024-01-15', type: 'storage' },
    { id: 2, title: 'Quality Sample', date: '2024-01-15', type: 'sample' },
    { id: 3, title: 'Packaging View', date: '2024-01-14', type: 'packaging' },
    { id: 4, title: 'Loading Process', date: '2024-01-13', type: 'loading' }
  ];

  const mockDocuments = [
    { id: 1, name: 'Quality Certificate', type: 'PDF', size: '2.3 MB', date: '2024-01-15' },
    { id: 2, name: 'ICUMSA Analysis Report', type: 'PDF', size: '1.8 MB', date: '2024-01-15' },
    { id: 3, name: 'Warehouse Receipt', type: 'PDF', size: '0.9 MB', date: '2024-01-14' },
    { id: 4, name: 'Origin Certificate', type: 'PDF', size: '1.2 MB', date: '2024-01-13' }
  ];

  const mockHistory = [
    { id: 1, action: 'Initial Deposit', date: '2024-01-10', location: lot.warehouse, txHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}` },
    { id: 2, action: 'Quality Inspection', date: '2024-01-12', location: lot.warehouse, txHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}` },
    { id: 3, action: 'Temperature Check', date: '2024-01-15', location: lot.warehouse, txHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}` },
    { id: 4, action: 'Inventory Update', date: '2024-01-16', location: lot.warehouse, txHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}` }
  ];

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <LotTitle>
            <h2>{lot.id}</h2>
            <div className="lot-subtitle">{lot.type} • ICUMSA {lot.icumsa} • {lot.quantity.toLocaleString()} kg</div>
          </LotTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <TabNavigation>
            <Tab 
              active={activeTab === 'overview'} 
              onClick={() => setActiveTab('overview')}
            >
              <FiBarChart />
              Overview
            </Tab>
            <Tab 
              active={activeTab === 'images'} 
              onClick={() => setActiveTab('images')}
            >
              <FiCamera />
              Images ({mockImages.length})
            </Tab>
            <Tab 
              active={activeTab === 'documents'} 
              onClick={() => setActiveTab('documents')}
            >
              <FiFileText />
              Documents ({mockDocuments.length})
            </Tab>
            <Tab 
              active={activeTab === 'blockchain'} 
              onClick={() => setActiveTab('blockchain')}
            >
              <FiHash />
              Blockchain
            </Tab>
          </TabNavigation>

          <TabContent>
            {activeTab === 'overview' && (
              <DetailGrid>
                <DetailSection>
                  <h3>
                    <FiMapPin />
                    Location & Storage
                  </h3>
                  <DetailItem>
                    <span className="label">Warehouse:</span>
                    <span className="value">{lot.warehouse}</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Origin:</span>
                    <span className="value">{lot.origin}</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Storage Section:</span>
                    <span className="value">A-{Math.floor(Math.random() * 99) + 1}</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Last Inspection:</span>
                    <span className="value">{lot.lastInspection}</span>
                  </DetailItem>
                </DetailSection>

                <DetailSection>
                  <h3>
                    <FiThermometer />
                    Environmental Conditions
                  </h3>
                  <DetailItem>
                    <span className="label">Temperature:</span>
                    <span className="value">{lot.temperature}°C</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Moisture:</span>
                    <span className="value">{(lot.moisture * 100).toFixed(1)}%</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Humidity:</span>
                    <span className="value">{Math.floor(Math.random() * 20) + 40}%</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Last Updated:</span>
                    <span className="value">2 hours ago</span>
                  </DetailItem>
                </DetailSection>

                <DetailSection>
                  <h3>
                    <FiShield />
                    Quality Metrics
                  </h3>
                  <DetailItem>
                    <span className="label">ICUMSA Rating:</span>
                    <span className="value">{lot.icumsa}</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Purity:</span>
                    <span className="value">{lot.purity}%</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Granulation:</span>
                    <span className="value">Medium</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Color Index:</span>
                    <span className="value">{lot.icumsa} IU</span>
                  </DetailItem>
                </DetailSection>

                <DetailSection>
                  <h3>
                    <FiTruck />
                    Logistics
                  </h3>
                  <DetailItem>
                    <span className="label">Total Quantity:</span>
                    <span className="value">{lot.quantity.toLocaleString()} kg</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Available:</span>
                    <span className="value">{(lot.quantity * 0.95).toLocaleString()} kg</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Reserved:</span>
                    <span className="value">{(lot.quantity * 0.05).toLocaleString()} kg</span>
                  </DetailItem>
                  <DetailItem>
                    <span className="label">Est. Value:</span>
                    <span className="value">${(lot.quantity * 1.2).toLocaleString()}</span>
                  </DetailItem>
                </DetailSection>
              </DetailGrid>
            )}

            {activeTab === 'images' && (
              <>
                <ImageGallery>
                  {mockImages.map(image => (
                    <ImageCard key={image.id}>
                      <div className="image-placeholder">
                        <FiCamera />
                      </div>
                      <div className="image-info">
                        <div className="image-title">{image.title}</div>
                        <div className="image-date">{image.date}</div>
                      </div>
                    </ImageCard>
                  ))}
                </ImageGallery>
              </>
            )}

            {activeTab === 'documents' && (
              <DocumentList>
                {mockDocuments.map(doc => (
                  <div key={doc.id} className="document-item">
                    <div className="doc-info">
                      <FiFileText className="doc-icon" />
                      <div className="doc-details">
                        <div className="doc-name">{doc.name}</div>
                        <div className="doc-meta">{doc.type} • {doc.size} • {doc.date}</div>
                      </div>
                    </div>
                    <div className="doc-actions">
                      <ActionButton>
                        <FiDownload />
                        Download
                      </ActionButton>
                      <ActionButton primary>
                        <FiExternalLink />
                        View
                      </ActionButton>
                    </div>
                  </div>
                ))}
              </DocumentList>
            )}

            {activeTab === 'blockchain' && (
              <>
                <BlockchainSection>
                  <div className="blockchain-header">
                    <FiHash />
                    Tamper-Evident Blockchain Record
                  </div>
                  <div className="hash-container">
                    <div className="hash-label">Current Hash:</div>
                    <div className="hash-value">{lot.blockchainHash}</div>
                  </div>
                  <div className="hash-container">
                    <div className="hash-label">Previous Hash:</div>
                    <div className="hash-value">cb{Math.floor(Math.random() * 100).toString().padStart(2, '0')}{Math.random().toString(16).substr(2, 40)}</div>
                  </div>
                  <ActionButton primary>
                    <FiExternalLink />
                    View on Blockchain Explorer
                  </ActionButton>
                </BlockchainSection>

                <DetailSection>
                  <h3>
                    <FiClock />
                    Transaction History
                  </h3>
                  {mockHistory.map(event => (
                    <DetailItem key={event.id}>
                      <div>
                        <div className="value">{event.action}</div>
                        <div className="label">{event.date} • {event.location}</div>
                      </div>
                      <ActionButton>
                        <FiHash />
                        {event.txHash}
                      </ActionButton>
                    </DetailItem>
                  ))}
                </DetailSection>
              </>
            )}
          </TabContent>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
}

export default LotDetailModal; 